import { Configuration, SkusApi } from '@/api';
import useGetSkuQuery from '@/api/queries/useGetSkuQuery';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { ProgressCallback } from '@/components/ImageUploadForm';
import { Image } from '@/models/Image';
import { SkuAssociation } from '@/models/Sku';
import { isSupplierCreatedByStore } from '@/models/Supplier';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import ProductForm from '@/modules/product/components/ProductForm';
import { SkuCreate, SkuUpdate } from '@/modules/product/models/Sku';
import { ProductListPath } from '@/modules/product/paths';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import { TruckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

const ProductEdit = () => {
  const navigate = useNavigate();

  const { skuId } = useParams();

  const { currentUser } = useAuth();

  const { supplierId, storeId } = currentUser();

  const { sku, isLoading, error, invalidateQuery } = useGetSkuQuery(skuId, [
    SkuAssociation.PRODUCT,
    SkuAssociation.PRICES,
    SkuAssociation.SUPPLIER,
  ]);

  const skusApi = new SkusApi(new Configuration(useAuth));

  const handleSubmit = async (skuUpdate: SkuCreate | SkuUpdate) => {
    if (!sku) {
      return;
    }

    const updatedSku: SkuUpdate = {
      ...skuUpdate,
      id: sku.id,
      productId: sku.productId,
    };

    try {
      await skusApi.updateSku(sku.id, updatedSku);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Product "${sku.name}" updated successfully!`}
          visible={t.visible}
        />
      ));

      await invalidateQuery();

      navigate(ProductListPath);
    } catch (error) {
      console.error('Error while updating product:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (!sku || error) {
    return;
  }

  const handleUploadImage = (
    file: File,
    index: number,
    callback: ProgressCallback,
  ): Promise<Image> => {
    return skusApi.uploadSkuImage(sku.id, file, index + 1, callback);
  };

  const handleDeleteImage = (index: number): Promise<void> => {
    return skusApi.deleteSkuImage(sku.id, index + 1);
  };

  // Show the supplier select field, when the supplier is created by the store (retailer)
  const showSupplierSelect = isSupplierCreatedByStore(sku.supplier, storeId);
  // Show the supplier text, when the user is not a supplier (retailer)
  const showSupplier = !supplierId && !showSupplierSelect;

  return (
    <>
      <SettingsHeader title="Edit Product" icon={<TruckIcon className="w-6 h-6" />} />

      <ProductForm
        sku={sku}
        onSubmit={handleSubmit}
        onUploadImage={handleUploadImage}
        onDeleteImage={handleDeleteImage}
        showSupplier={showSupplier}
        showSupplierSelect={showSupplierSelect}
        selectSupplierCreatedByStoreId={storeId}
      />
    </>
  );
};

export default ProductEdit;
